import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaInstagram,
  FaFacebook,
  FaTwitter,
  FaLinkedin,
  FaYoutube,
} from 'react-icons/fa';
import {
  DOCUMENTATION_URL,
  SOCIAL_LINKS,
  SUPPORT_EMAIL,
  PARTNER_EMAIL,
} from '../config/appConfig';

// Public canonical site URL (matches <link rel="canonical"> in public/index.html).
const SITE_URL = 'https://shop.neobox.ca';

const SOCIAL_ICONS = [
  { key: 'instagram', Icon: FaInstagram, label: 'Instagram' },
  { key: 'facebook', Icon: FaFacebook, label: 'Facebook' },
  { key: 'twitter', Icon: FaTwitter, label: 'Twitter / X' },
  { key: 'linkedin', Icon: FaLinkedin, label: 'LinkedIn' },
  { key: 'youtube', Icon: FaYoutube, label: 'YouTube' },
];

/**
 * Organization structured data (schema.org JSON-LD).
 *
 * Helps search engines associate the brand with its logo, official site, and
 * verified social profiles (Knowledge Graph / rich results). Rendered in the
 * footer; Google reads JSON-LD anywhere in the document, so no <head> injection
 * (and therefore no react-helmet dependency) is required.
 */
const buildOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Neobox Inc',
  url: `${SITE_URL}/`,
  logo: `${SITE_URL}/logo512.png`,
  // sameAs links the brand to its official social profiles.
  sameAs: Object.values(SOCIAL_LINKS).filter(Boolean),
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    email: SUPPORT_EMAIL,
    areaServed: 'CA',
    availableLanguage: ['French', 'English'],
  },
});

const Footer = () => {
  const { t } = useTranslation();

  return (
    <footer className="bg-black px-4 pb-[calc(5rem+env(safe-area-inset-bottom,0px))] lg:pb-0">
      {/* schema.org Organization data — brand, logo, official site, social profiles.
          Content is built from trusted static config (no user input). We still
          escape "<" so a stray "</script>" in a config value can never break out
          of the JSON-LD block (defense-in-depth, the standard JSON-LD pattern). */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(buildOrganizationSchema()).replace(
            /</g,
            '\\u003c',
          ),
        }}
      />

      <div className="max-w-[1100px] mx-auto w-full pt-10 md:pt-24">
        {/* Top section: copyright + links */}
        <div className="flex flex-col md:flex-row justify-between gap-8 md:gap-4">
          {/* Left: copyright */}
          <div className="w-full md:w-auto">
            <p className="text-white text-sm text-center md:text-left">
              {t('copyright')}
            </p>
          </div>

          {/* Right: primary footer navigation (semantic <nav> for SEO/a11y) */}
          <nav
            aria-label={t('footerNavLabel')}
            className="w-full md:w-auto md:ml-auto"
          >
            <ul className="flex flex-col gap-2">
              <li>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="text-white text-sm hover:underline"
                >
                  {t('Contact Us')}
                </a>
              </li>
              <li>
                <a
                  href={DOCUMENTATION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white text-sm hover:underline"
                >
                  {t('Documentation')}
                </a>
              </li>
              <li>
                <Link
                  to="/terms-of-use"
                  className="text-white text-sm hover:underline"
                >
                  {t('Terms of Use')}
                </Link>
              </li>
              <li>
                <a
                  href={`mailto:${PARTNER_EMAIL}`}
                  className="text-white text-sm hover:underline"
                >
                  {t('Become Partner')}
                </a>
              </li>
            </ul>
          </nav>
        </div>

        {/* Divider */}
        <div className="border-t border-[#ccc] mt-8 md:mt-20"></div>

        {/* Bottom section: social icons + powered by */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 md:gap-0 mt-6 text-[#999]">
          <nav aria-label={t('footerSocialLabel')} className="flex gap-4">
            {SOCIAL_ICONS.map(({ key, Icon, label }) =>
              SOCIAL_LINKS[key] ? (
                <a
                  key={key}
                  href={SOCIAL_LINKS[key]}
                  target="_blank"
                  rel="me noopener noreferrer"
                  aria-label={`Neobox ${label}`}
                  title={`Neobox ${label}`}
                  className="text-lg hover:text-gray-300 hover:scale-125 transition-transform inline-flex items-center justify-center min-w-[44px] min-h-[44px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white rounded"
                >
                  <Icon aria-hidden="true" focusable="false" />
                  <span className="sr-only">{`Neobox ${label}`}</span>
                </a>
              ) : null,
            )}
          </nav>

          <p className="text-white text-sm py-2 md:py-6 text-center md:text-left">
            {t('poweredBy')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
